<script setup>
import {onMounted, ref, watch} from 'vue';
import {useI18n} from "vue-i18n";
import {useFetch} from "@/composables/useFetch.js";
import {uri} from "@/constants/config.js";
import BaseDialog from "@/base-components/Dialog/BaseDialog.vue";
import Button from "@/base-components/Button";
import {Form} from "vee-validate";
import BaseInputSelect from "@/base-components/Form/BaseInputSelect.vue";
import InputText from "@/base-components/Form/InputText.vue";
import {object} from "yup";
import useValidate from "@/composables/useValidate.js";
import {FormSwitch} from "@/base-components/Form";

const props = defineProps({
  show: Boolean,
})
let request = ref({
  title: null,
  name: null,
  adepter: null,
  active:false
})
const {t: $t} = useI18n()
const emit = defineEmits(['close', 'onSuccess']);

function close() {
  emit('close')
}

const ipgPluginListLoadingFlag = ref(true)
const pluginList = ref([])
const plugin = ref(null)
const validationList = ref(['adapter', 'title', 'name'])
const validationSchema = ref(null);
const configFields = ref([])

const listPluginsRequest = async () => {
  ipgPluginListLoadingFlag.value = true
  let plugins = null
  try {
    const {data, error} = await useFetch(`${uri.admin.financial.ipg.plugin.list}`, {method: "GET"});
    if (data) {
      plugins = data
    }
  } catch (err) {
    console.log(err)
  }
  return plugins
}

const submit = () => {
  let data = {
    adapterConfig: {}
  }
  data.name = request.value.name
  data.displayName = request.value.title
  data.plugin = plugin.value.adapter
  configFields.value.forEach((item) => {
    data.adapterConfig[item.name] = request.value[item.name]
  })
  data.active = request.value.active
  createIPG(data)
}

async function createIPG(request) {
  ipgPluginListLoadingFlag.value = true
  try {
    const {data, error} = await useFetch(`${uri.admin.financial.ipg.create}`, {method: "POST", body: request});
    if (data) {
      emit('onSuccess')
    }
  } catch (err) {
    console.log(err)
  }

  ipgPluginListLoadingFlag.value = false
}

const fetchData = async () => {
  try {
    const [pluginsData] = await Promise.all([
      listPluginsRequest(),
    ]);
    pluginList.value = pluginsData;
  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    ipgPluginListLoadingFlag.value = false
  }
}

function setValidation() {
  validationSchema.value = object(useValidate(validationList.value))
}

// Fetch data when the component mounts
onMounted(fetchData);
watch(plugin, (value) => {
  validationList.value = ['adapter', 'title', 'name']
  if (value) {
    request.value.title = plugin.value.displayName
    request.value.name = plugin.value.adapter
    configFields.value = plugin.value.config
    validationList.value = [...validationList.value, ...configFields.value.map(item => item.name)]
  }
  setValidation()
})
setValidation()
</script>

<template>
  <section>
    <BaseDialog
        :size="'lg'"
        staticBackdrop
        :open="show"
        :has-footer="true"
        :title="$t('title.add-ipg')"
        @close="close"
    >
      <div class="rounded-md px-2 py-2 w-full max-h-[78vh] min-h-72 overflow-auto mb-5">
        <Form id="myForm" :validation-schema="validationSchema" @submit="submit">
          <div class="grid grid-cols-2 gap-3">
            <div class="col-span-2 md:col-span-2 lg:col-span-2 xl:col-span-2">
              <BaseInputSelect
                  :has-label="true"
                  :title="$t('label.plugin')"
                  class="primary"
                  v-model="plugin"
                  name="adapter"
                  deselect-label=""
                  :has-default-error-message="true"
                  :is-multiple="false"
                  track-by="adapter"
                  label="adapter"
                  :options="pluginList"
                  :searchable="false"
                  :allow-empty="false"
                  :is-preselect-first="true"
              >
              </BaseInputSelect>
            </div>
            <div class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1">
              <InputText
                  name="title"
                  :has-label="true"
                  :label="$t('label.title')"
                  v-model="request.title"
                  id="input-wizard-3"
                  type="text"
              />
            </div>
            <div class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1">
              <InputText
                  name="name"
                  :has-label="true"
                  :label="$t('label.name')"
                  v-model="request.name"
                  id="input-wizard-3"
                  type="text"
              />
            </div>
            <div class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1" v-for="(config,index) in configFields"
                 :key="index">
              <InputText
                  :name="config.name"
                  :has-label="true"
                  :label="$t(`label.${config.name}`)"
                  v-model="request[config.name]"
                  id="input-wizard-3"
                  type="text"
              />
            </div>
            <div class="col-span-2 md:col-span-2 lg:col-span-2 xl:col-span-2">
              <div class="w-32">
                  <span class="text-sm mb-1">
                    {{ $t('label.status') }}
                  </span>
                <FormSwitch class="flex flex-row items-start my-2" dir="ltr">
                  <div class="px-2">
                    {{request.active?$t('title.active'):$t('title.inactive')}}
                  </div>
                  <FormSwitch.Input  id="post-form-6" type="checkbox" class="xs absolute end-8" v-model="request.active"/>
                </FormSwitch>
              </div>
            </div>

          </div>
        </Form>
      </div>
      <template v-slot:footer>
        <div class="flex col-md-12 items-end justify-end gap-2" v-if="ipgPluginListLoadingFlag">
          <free-style-shimmer width="100%" height="25px" class="w-full"/>
        </div>
        <div class="flex col-md-12 items-end justify-end gap-2" v-else>
          <Button variant="outline-secondary" @click="close" size="sm" class=" shadow-md justify-end items-end">
            {{ $t('button.cancel') }}
          </Button>
          <Button variant="primary" size="sm" form="myForm" class=" shadow-md justify-end items-end">
            {{ $t('button.save') }}
          </Button>
        </div>
      </template>

    </BaseDialog>
  </section>
</template>

<style scoped>

</style>